import React, { useState, useEffect } from 'react';
import EventTable from './EventTable';
import EventForm from './EventForm';
import AdminStats from './AdminStats';
import BulkImport from './BulkImport';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);

  // Load events on component mount
  useEffect(() => {
    loadEvents();
  }, []);

  // Log event count changes for debugging
  useEffect(() => {
    console.log('📊 Admin Dashboard: Event count updated to', events.length);
  }, [events]);

  const loadEvents = async () => {
    setIsLoading(true);
    try {
      // Load from the same source as the main app
      const response = await fetch('/events_master_with_format_and_geo.json');
      if (response.ok) {
        const dcEvents = await response.json();
        
        // Load NYC events from the module
        const { NYC_MASTER_LOCATIONS } = await import('../data/nycMasterLocations');
        
        // Combine all events
        const allEvents = [
          ...dcEvents.map(event => ({ ...event, region: 'DC' })),
          ...NYC_MASTER_LOCATIONS
        ];
        
        setEvents(allEvents);
        console.log('📊 Admin: Loaded', allEvents.length, 'events');
      } else {
        console.error('Failed to load events');
        setEvents([]);
      }
    } catch (error) {
      console.error('Error loading events:', error);
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setShowEventForm(true);
  };

  const handleBulkImport = () => {
    setShowBulkImport(true);
  };

  const handleBulkImportComplete = (importedEvents) => {
    setEvents(prev => [...prev, ...importedEvents]);
    setShowBulkImport(false);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setShowEventForm(true);
  };

  const handleSaveEvent = (eventData) => {
    if (editingEvent) {
      // Update existing event
      setEvents(prev => prev.map(e => e.id === editingEvent.id ? { ...e, ...eventData } : e));
    } else {
      // Create new event
      const newEvent = {
        id: `event-${Date.now()}`,
        ...eventData,
        region: eventData.region || 'DC'
      };
      setEvents(prev => [...prev, newEvent]);
    }
    setShowEventForm(false);
    setEditingEvent(null);
  };

  const handleDeleteEvent = (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      setEvents(prev => prev.filter(e => e.id !== eventId));
    }
  };

  const handleClearAllEvents = () => {
    console.log('🗑️ Clear All Events button clicked!');
    const eventCount = events.length;
    console.log(`📊 Current event count: ${eventCount}`);
    
    const confirmed = window.confirm(
      `Are you sure you want to delete ALL ${eventCount} events? This action cannot be undone.`
    );
    
    if (confirmed) {
      const doubleConfirmed = window.confirm(
        `FINAL WARNING: This will permanently delete all ${eventCount} events. Type "DELETE ALL" to confirm.`
      );
      
      if (doubleConfirmed) {
        setEvents([]);
        console.log(`🗑️ Cleared all ${eventCount} events`);
        alert(`Successfully deleted all ${eventCount} events!`);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_authenticated');
    window.location.reload();
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading events...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-title">
            <h1>🎯 Event Management</h1>
            <p>Manage your events database</p>
          </div>
          <div className="admin-actions">
            <button 
              onClick={handleCreateEvent}
              className="btn-primary"
            >
              ➕ Add Event
            </button>
            <button 
              onClick={handleBulkImport}
              className="btn-secondary"
            >
              📊 Bulk Import
            </button>
            <button 
              onClick={handleClearAllEvents}
              className="btn-danger"
              style={{ 
                display: 'block', 
                visibility: 'visible',
                opacity: 1,
                zIndex: 10
              }}
            >
              🗑️ Clear All Events
            </button>
            <button 
              onClick={handleLogout}
              className="btn-secondary"
            >
              🚪 Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="admin-nav">
        <button 
          className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 Dashboard
        </button>
        <button 
          className={`nav-btn ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          📅 Events ({events.length})
        </button>
        <button 
          className={`nav-btn ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Settings
        </button>
      </nav>

      {/* Main Content */}
      <main className="admin-main">
        {activeTab === 'dashboard' && (
          <AdminStats events={events} />
        )}
        
        {activeTab === 'events' && (
          <EventTable 
            events={events}
            onEdit={handleEditEvent}
            onDelete={handleDeleteEvent}
            onRefresh={loadEvents}
          />
        )}
        
        {activeTab === 'settings' && (
          <div className="admin-settings">
            <h2>Settings</h2>
            <p>Admin settings and configuration options will be available here.</p>
          </div>
        )}
      </main>

      {/* Event Form Modal */}
      {showEventForm && (
        <EventForm
          event={editingEvent}
          onSave={handleSaveEvent}
          onCancel={() => {
            setShowEventForm(false);
            setEditingEvent(null);
          }}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <BulkImport
          onImport={handleBulkImportComplete}
          onCancel={() => setShowBulkImport(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
